import {Plugins, Variable, EvalEnv, Expressions} from '@packages/systems/wfdl';
import {EText, ECall, type Expression} from '@packages/systems/wfdl-ast';
import {qualify, identifier} from '@packages/systems/wfdl-Qualified';
import {Price} from '@packages/systems/ui/icons';
import {
  FieldImageSetIcon,
  CMSSandwichIcon,
  CMSInsertIcon,
  DateIcon,
  EmailIcon,
  LinkIcon,
  PhoneIcon,
  AttachmentIcon,
} from '@packages/systems/spring';
import {
  DateType,
  DateTimeType,
  DateFormatType,
  dynamoDateToDate,
  dynamoDateToDateWithTimezone,
  dynamoDateTimeToDate,
  dynamoDateTimeToDateWithTimezone,
  dynamoDateToListOfElements,
  dynamoDateToListOfElementsWithTimezone,
  dynamoDateToText,
  dynamoDateToTextWithTimezone,
  dynamoDateTimeToListOfElements,
  dynamoDateTimeToListOfElementsWithTimezone,
  dynamoDateTimeToText,
  dynamoDateTimeToTextWithTimezone,
  dynamoDateIs,
  dynamoDateIsWithTimezone,
  dynamoDateIsNot,
  dynamoDateIsNotWithTimezone,
  dynamoDateIsBefore,
  dynamoDateIsBeforeWithTimezone,
  dynamoDateIsOnOrBefore,
  dynamoDateIsOnOrBeforeWithTimezone,
  dynamoDateIsAfter,
  dynamoDateIsAfterWithTimezone,
  dynamoDateIsOnOrAfter,
  dynamoDateIsOnOrAfterWithTimezone,
  dynamoDateIsSet,
  dynamoDateIsNotSet,
  dynamoDateOperand,
  dynamoDateTimeIs,
  dynamoDateTimeIsWithTimezone,
  dynamoDateTimeIsNot,
  dynamoDateTimeIsNotWithTimezone,
  dynamoDateTimeIsBefore,
  dynamoDateTimeIsBeforeWithTimezone,
  dynamoDateTimeIsOnOrBefore,
  dynamoDateTimeIsOnOrBeforeWithTimezone,
  dynamoDateTimeIsAfter,
  dynamoDateTimeIsAfterWithTimezone,
  dynamoDateTimeIsOnOrAfter,
  dynamoDateTimeIsOnOrAfterWithTimezone,
  dynamoDateTimeIsSet,
  dynamoDateTimeIsNotSet,
  dynamoDateTimeOperand,
} from './Date';
import {
  OptionType,
  dynamoOptionToText,
  dynamoOptionToListOfElements,
  dynamoOptionCondition,
  dynamoOptionIsAny,
  dynamoOptionIsNotAny,
  dynamoOptionIsSet,
  dynamoOptionIsNotSet,
} from './Option';
import {
  PlainTextType,
  dynamoPlainTextToListOfElements,
  dynamoPlainTextToText,
  dynamoPlainTextEquals,
  dynamoPlainTextDoesNotEquals,
  dynamoPlainTextContains,
  dynamoPlainTextDoesNotContain,
  dynamoPlainTextStartsWith,
  dynamoPlainTextDoesNotStartWith,
  dynamoPlainTextEndsWith,
  dynamoPlainTextDoesNotEndWith,
  dynamoPlainTextIsSet,
  dynamoPlainTextIsNotSet,
  dynamoPlainTextOperand,
} from './PlainText';
import {
  EmailType,
  dynamoEmailToListOfElements,
  dynamoEmailToText,
  dynamoEmailToBasicLink,
  dynamoEmailToBasicLinkV2,
  dynamoEmailContains,
  dynamoEmailDoesNotContain,
  dynamoEmailEquals,
  dynamoEmailDoesNotEqual,
  dynamoEmailStartsWith,
  dynamoEmailDoesNotStartWith,
  dynamoEmailEndsWith,
  dynamoEmailDoesNotEndWith,
  dynamoEmailIsSet,
  dynamoEmailIsNotSet,
  dynamoEmailOperand,
} from './Email';
import {
  PhoneType,
  dynamoPhoneToText,
  dynamoPhoneToListOfElements,
  dynamoPhoneToBasicLink,
  dynamoPhoneContains,
  dynamoPhoneDoesNotContain,
  dynamoPhoneEquals,
  dynamoPhoneDoesNotEqual,
  dynamoPhoneStartsWith,
  dynamoPhoneDoesNotStartWith,
  dynamoPhoneEndsWith,
  dynamoPhoneDoesNotEndWith,
  dynamoPhoneIsSet,
  dynamoPhoneIsNotSet,
  dynamoPhoneOperand,
} from './Phone';
import {
  VideoLinkType,
  dynamoVideoLinkToListOfElements,
  dynamoVideoLinkToListOfLightboxMedia,
  dynamoVideoLinkToText,
  dynamoVideoLinkToBasicLink,
  dynamoVideoLinkToBasicLinkV2,
  dynamoVideoLinkToVideo,
} from './VideoLink';
import {
  LinkType,
  dynamoLinkToListOfElements,
  dynamoLinkToText,
  dynamoLinkToBasicLink,
  dynamoLinkToBasicLinkV2,
} from './Link';
import {
  NumberType,
  dynamoNumberEquals,
  dynamoNumberDoesNotEqual,
  dynamoNumberIsGreaterThan,
  dynamoNumberIsGreaterThanEqualTo,
  dynamoNumberIsLessThan,
  dynamoNumberIsLessThanEqualTo,
  dynamoNumberIsSet,
  dynamoNumberIsNotSet,
  dynamoNumberOperand,
  dynamoNumberToListOfElements,
  dynamoNumberToText,
  dynamoNumberToNumber,
} from './Number';
import {
  FullSlugType,
  dynamoFullSlugToBasicLink,
  dynamoFullSlugToBasicLinkV2,
  dynamoFullSlugToText,
} from './FullSlug';
import {SlugType, dynamoSlugToText, dynamoSlugToListOfElements} from './Slug';
import {
  FileRefType,
  dynamoFileRefToBasicLink,
  dynamoFileRefToText,
  dynamoFileRefToBasicLinkV2,
} from './FileRef';
import {
  ImageType,
  MultiImageType,
  dynamoImageToImage,
  dynamoImageToText,
  dynamoImageToListOfElements,
  dynamoImageToListOfLightboxMedia,
  listOfDynamoImageToListOfLightboxMedia,
  dynamoMultiImageIsSet,
  dynamoMultiImageIsNotSet,
  dynamoMultiImageOperand,
} from './Image';
import {
  BooleanType,
  dynamoBooleanDoesNotEqual,
  dynamoBooleanEquals,
  dynamoBooleanOperand,
  dynamoBooleanToBoolean,
  dynamoBooleanToText,
  dynamoBooleanToTriStateBoolean,
  dynamoBooleanToUnionBoolean,
  dynamoBooleanToVisibilityConditions,
} from './Boolean';
import {dynamoColorToText} from './Color';
import {
  CurrentItemRefType,
  dynamoCurrentItemRefCondition,
  dynamoCurrentItemRefIsAny,
  dynamoCurrentItemRefIsNotAny,
} from './CurrentItemRef';
import {
  ItemRefType,
  dynamoItemRefCondition,
  dynamoItemRefIsAny,
  dynamoItemRefIsNotAny,
  dynamoItemRefIsNotSet,
  dynamoItemRefIsSet,
} from './ItemRef';
import {
  ItemRefSetType,
  dynamoItemRefSetCondition,
  dynamoItemRefSetIsNotSet,
  dynamoItemRefSetIsSet,
  dynamoItemRefSetContainsAny,
  dynamoItemRefSetDoesNotContainAny,
  dynamoItemRefSetCountEquals,
  dynamoItemRefSetCountDoesNotEqual,
  dynamoItemRefSetCountIsGreaterThan,
  dynamoItemRefSetCountIsGreaterThanEqualTo,
  dynamoItemRefSetCountIsLessThan,
  dynamoItemRefSetCountIsLessThanEqualTo,
} from './ItemRefSet';
import {
  RichTextType,
  dynamoRichTextToRichTextChildren,
  dynamoRichTextIsSet,
  dynamoRichTextIsNotSet,
  dynamoRichTextOperand,
} from './RichText';
import {CommercePriceType} from './CommercePrice';

import {
  RefAnyOfConditionValueType,
  RefConditionValueType,
  RefValueType,
} from './dataTypes/RefAnyOfConditionValue';
import {
  OptionAnyOfConditionValueType,
  OptionAnyOfListItemType,
} from './dataTypes/OptionAnyOfConditionValue';
import {AnyOfListItemType} from './dataTypes/AnyOfListItem';

export {
  dynamoPriceToListOfElements,
  dynamoPriceToText,
  addDynamoUtilsToEnv,
} from './utils';

import {createIsSetValues} from './conditions';
import {
  TDynamoFileRef,
  TDynamoImage,
  TDynamoLink,
  TDynamoVideoLink,
} from '@packages/systems/plugins-utils/DynamoGateway';

const replaceableValues: Array<{
  variableType: Variable.VariableType;
  exprFun: Expression;
}> = [
  {
    variableType: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoDateTimeToListOfElements')
      )
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateToListOfElementsWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoDateTimeToText'))
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateToTextWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoDateTimeToDate'))
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateTimeToDateWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoDateIs'))
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateIsWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoDateIsNot'))
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateIsNotWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoDateIsBefore'))
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateIsBeforeWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoDateIsOnOrBefore')
      )
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateIsOnOrBeforeWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoDateIsAfter'))
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateIsAfterWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoDateIsOnOrAfter')
      )
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateIsOnOrAfterWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoDateTimeIs'))
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateTimeIsWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(Plugins.name('DynamoGateway'), identifier('dynamoDateTimeIsNot'))
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateTimeIsNotWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoDateTimeIsBefore')
      )
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateTimeIsBeforeWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoDateTimeIsOnOrBefore')
      )
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateTimeIsOnOrBeforeWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoDateTimeIsAfter')
      )
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateTimeIsAfterWithTimezone'
    ),
  },
  {
    variableType: Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        identifier('dynamoDateTimeIsOnOrAfter')
      )
    ),
    exprFun: Expressions.qualifiedVariable(
      'DynamoGateway',
      'dynamoDateTimeIsOnOrAfterWithTimezone'
    ),
  },
];

// In order to change the timezone our evaluationEnv
// assumes for us _after_ Plugins.bootstrap we need
// to re-evaluate the functions where timezone is being
// used with the new timezone values that we want to use.
export const evaluateAndReplaceTimezoneFunctions = (
  env: EvalEnv.EvalEnvType,
  timezone: string
): EvalEnv.EvalEnvType => {
  return replaceableValues.reduce((acc, curr) => {
    return EvalEnv.insertValue(
      acc,
      curr.variableType,
      Expressions.evaluate(
        acc,
        ECall({
          fun: curr.exprFun,
          arg: EText(timezone),
        })
      )
    );
  }, env);
};

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    Date: DateType,
    DateTime: DateTimeType,
    DateFormat: DateFormatType,
    Option: OptionType,
    PlainText: PlainTextType,
    Email: EmailType,
    Phone: PhoneType,
    VideoLink: VideoLinkType,
    Link: LinkType,
    Number: NumberType,
    Image: ImageType,
    Boolean: BooleanType,
    CurrentItemRef: CurrentItemRefType,
    ItemRef: ItemRefType,
    ItemRefSet: ItemRefSetType,
    FullSlug: FullSlugType,
    Slug: SlugType,
    FileRef: FileRefType,
    RichText: RichTextType,
    CommercePrice: CommercePriceType,
    MultiImage: MultiImageType,
    RefAnyOfConditionValue: RefAnyOfConditionValueType,
    RefConditionValue: RefConditionValueType,
    RefValue: RefValueType,
    OptionAnyOfConditionValue: OptionAnyOfConditionValueType,
    OptionAnyOfListItem: OptionAnyOfListItemType,
    AnyOfListItem: AnyOfListItemType,
  },
  icons: {
    DateTime: DateIcon,
    Email: EmailIcon,
    ItemRef: CMSInsertIcon,
    ItemRefSet: CMSSandwichIcon,
    Link: LinkIcon,
    Phone: PhoneIcon,
    Price,
    MultiImage: FieldImageSetIcon,
    FileRef: AttachmentIcon,
  },
  values: {
    dynamoDateToDate,
    dynamoDateToDateWithTimezone,
    dynamoDateTimeToDate,
    dynamoDateTimeToDateWithTimezone,
    dynamoDateToText,
    dynamoDateToTextWithTimezone,
    dynamoDateToListOfElements,
    dynamoDateToListOfElementsWithTimezone,
    dynamoDateTimeToText,
    dynamoDateTimeToTextWithTimezone,
    dynamoDateTimeToListOfElements,
    dynamoDateTimeToListOfElementsWithTimezone,
    dynamoPlainTextToText,
    dynamoPlainTextToListOfElements,
    dynamoEmailToText,
    dynamoEmailToListOfElements,
    dynamoEmailToBasicLink,
    dynamoEmailToBasicLinkV2,
    dynamoPhoneToText,
    dynamoPhoneToListOfElements,
    dynamoPhoneToBasicLink,
    dynamoVideoLinkToText,
    dynamoVideoLinkToListOfElements,
    dynamoVideoLinkToBasicLink,
    dynamoVideoLinkToBasicLinkV2,
    dynamoVideoLinkToVideo,
    dynamoOptionToText,
    dynamoOptionToListOfElements,
    dynamoLinkToListOfElements,
    dynamoVideoLinkToListOfLightboxMedia,
    dynamoLinkToText,
    dynamoLinkToBasicLink,
    dynamoLinkToBasicLinkV2,
    dynamoNumberToListOfElements,
    dynamoNumberToText,
    dynamoNumberToNumber,
    dynamoImageToImage,
    dynamoImageToText,
    dynamoImageToListOfElements,
    dynamoImageToListOfLightboxMedia,
    listOfDynamoImageToListOfLightboxMedia,
    dynamoBooleanToBoolean,
    dynamoBooleanToText,
    dynamoColorToText,
    dynamoBooleanToTriStateBoolean,
    dynamoBooleanToUnionBoolean,
    dynamoBooleanToVisibilityConditions,
    dynamoFullSlugToBasicLink,
    dynamoFullSlugToBasicLinkV2,
    dynamoFullSlugToText,
    dynamoSlugToText,
    dynamoSlugToListOfElements,
    dynamoFileRefToBasicLink,
    dynamoFileRefToText,
    dynamoFileRefToBasicLinkV2,
    dynamoRichTextToRichTextChildren,
    ...createIsSetValues('Link', TDynamoLink),
    ...createIsSetValues('File', TDynamoFileRef),
    ...createIsSetValues('Video', TDynamoVideoLink),
    ...createIsSetValues('Image', TDynamoImage),
    dynamoMultiImageIsSet,
    dynamoMultiImageIsNotSet,
    dynamoMultiImageOperand,
    dynamoBooleanEquals,
    dynamoBooleanDoesNotEqual,
    dynamoBooleanOperand,
    dynamoNumberIsSet,
    dynamoNumberIsNotSet,
    dynamoNumberEquals,
    dynamoNumberDoesNotEqual,
    dynamoNumberIsGreaterThan,
    dynamoNumberIsGreaterThanEqualTo,
    dynamoNumberIsLessThan,
    dynamoNumberIsLessThanEqualTo,
    dynamoNumberOperand,
    dynamoPlainTextIsSet,
    dynamoPlainTextIsNotSet,
    dynamoPlainTextEquals,
    dynamoPlainTextDoesNotEquals,
    dynamoPlainTextContains,
    dynamoPlainTextDoesNotContain,
    dynamoPlainTextStartsWith,
    dynamoPlainTextDoesNotStartWith,
    dynamoPlainTextEndsWith,
    dynamoPlainTextDoesNotEndWith,
    dynamoPlainTextOperand,
    dynamoEmailIsSet,
    dynamoEmailIsNotSet,
    dynamoEmailContains,
    dynamoEmailDoesNotContain,
    dynamoEmailEquals,
    dynamoEmailDoesNotEqual,
    dynamoEmailStartsWith,
    dynamoEmailDoesNotStartWith,
    dynamoEmailEndsWith,
    dynamoEmailDoesNotEndWith,
    dynamoEmailOperand,
    dynamoPhoneIsSet,
    dynamoPhoneIsNotSet,
    dynamoPhoneEquals,
    dynamoPhoneDoesNotEqual,
    dynamoPhoneContains,
    dynamoPhoneDoesNotContain,
    dynamoPhoneStartsWith,
    dynamoPhoneDoesNotStartWith,
    dynamoPhoneEndsWith,
    dynamoPhoneDoesNotEndWith,
    dynamoPhoneOperand,
    dynamoItemRefSetCondition,
    dynamoItemRefSetIsSet,
    dynamoItemRefSetIsNotSet,
    dynamoItemRefSetContainsAny,
    dynamoItemRefSetDoesNotContainAny,
    dynamoItemRefSetCountEquals,
    dynamoItemRefSetCountDoesNotEqual,
    dynamoItemRefSetCountIsGreaterThan,
    dynamoItemRefSetCountIsGreaterThanEqualTo,
    dynamoItemRefSetCountIsLessThan,
    dynamoItemRefSetCountIsLessThanEqualTo,
    dynamoDateIsSet,
    dynamoDateIsNotSet,
    dynamoDateIs,
    dynamoDateIsWithTimezone,
    dynamoDateIsNot,
    dynamoDateIsNotWithTimezone,
    dynamoDateIsBefore,
    dynamoDateIsBeforeWithTimezone,
    dynamoDateIsOnOrBefore,
    dynamoDateIsOnOrBeforeWithTimezone,
    dynamoDateIsAfter,
    dynamoDateIsAfterWithTimezone,
    dynamoDateIsOnOrAfter,
    dynamoDateIsOnOrAfterWithTimezone,
    dynamoDateOperand,
    dynamoDateTimeIsSet,
    dynamoDateTimeIsNotSet,
    dynamoDateTimeIs,
    dynamoDateTimeIsWithTimezone,
    dynamoDateTimeIsNot,
    dynamoDateTimeIsNotWithTimezone,
    dynamoDateTimeIsBefore,
    dynamoDateTimeIsBeforeWithTimezone,
    dynamoDateTimeIsOnOrBefore,
    dynamoDateTimeIsOnOrBeforeWithTimezone,
    dynamoDateTimeIsAfter,
    dynamoDateTimeIsAfterWithTimezone,
    dynamoDateTimeIsOnOrAfter,
    dynamoDateTimeIsOnOrAfterWithTimezone,
    dynamoDateTimeOperand,
    dynamoOptionCondition,
    dynamoOptionIsSet,
    dynamoOptionIsNotSet,
    dynamoOptionIsAny,
    dynamoOptionIsNotAny,
    dynamoRichTextIsSet,
    dynamoRichTextIsNotSet,
    dynamoRichTextOperand,
    dynamoItemRefCondition,
    dynamoItemRefIsSet,
    dynamoItemRefIsNotSet,
    dynamoItemRefIsAny,
    dynamoItemRefIsNotAny,
    dynamoCurrentItemRefCondition,
    dynamoCurrentItemRefIsAny,
    dynamoCurrentItemRefIsNotAny,
  },
});
